import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  inject,
  DestroyRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { debounceTime, switchMap, map, filter } from 'rxjs';
import { NgxFileDropEntry, FileSystemFileEntry, FileSystemDirectoryEntry } from 'ngx-file-drop';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FolderResource, FileResource } from 'app/shared/modules/folders/folders.types';
import { FoldersTabCreateFolderComponent } from 'app/shared/modules/folders/create-folder/create-folder.component';
import { SharedResourceViewFileComponent } from 'app/modules/shared-resources/folders/view-file/view-file.component';
import { FoldersTabFolderSharingComponent } from 'app/shared/modules/folders/folder-sharing/folder-sharing.component';
import { SharedResourcesService } from 'app/modules/shared-resources/shared-resources.service';
import { SharedResource } from 'app/modules/shared-resources/shared-resources.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FolderUtils } from '@shared/Utils/folderUtils';

@Component({
  selector: 'shared-resource-folders-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SharedResourceFoldersListComponent {
  sharedResource: SharedResource;
  folder: FolderResource;
  childFolders: FolderResource[];
  files: FileResource[];
  filesCount: any;
  selectedFile: FileResource;
  loading: boolean = false;
  searchInputControl: UntypedFormControl;
  results: { file_resources: []; folder_resources: [] } = { file_resources: [], folder_resources: [] };
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;
  drawerMode: 'over' | 'side';
  drawerOpened: boolean;

  private _numFilesUploaded: number = 0;
  private _fileTree: any[] = [];
  private destroyRef = inject(DestroyRef);

  @ViewChild('emptyItem', { static: true }) emptyItem: ElementRef;
  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;

  constructor(
    private _foldersTabService: FoldersTabService,
    private _matDialog: MatDialog,
    private _sharedResourcesService: SharedResourcesService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private confirmService: ConfirmService,
  ) {
    this.filesCount = {
      completed: 0,
      incomplete: 0,
      total: 0,
    };

    this.drawerMode = 'side';
    this.drawerOpened = true;

    this.searchInputControl = new UntypedFormControl();
  }

  ngOnInit(): void {
    this._sharedResourcesService.sharedResource$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sharedResource: SharedResource) => {
        this.sharedResource = sharedResource;
        this._changeDetectorRef.markForCheck();
      });

    this._sharedResourcesService.folder$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((folder: FolderResource) => {
        this.folder = folder;
        this.childFolders = folder.children;
        // This will be going to remove Once API start giving me sorted ranks
        this.files = folder.file_resources.sort((a, b) => a.rank - b.rank);
        // This will be going to remove once API send default ranking
        this.files.map((file, i) => (file.rank = i));
        this._fileTree = [];
        this._numFilesUploaded = 0;
        this._changeDetectorRef.markForCheck();
      });

    // Subscribe to search input field value changes
    this.searchInputControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        switchMap((query) => {
          return this._foldersTabService.searchFolders(query);
        }),
        map((response) => {
          this.results = response;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe();
  }

  createFolder(folderId): void {
    this._matDialog
      .open(FoldersTabCreateFolderComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          folderId: folderId,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this._router.navigate(['../' + result.folder.id], { relativeTo: this._activatedRoute });
      });
  }

  viewFile(fileResource): void {
    this._matDialog.open(SharedResourceViewFileComponent, {
      panelClass: 'mailbox-compose-dialog',
      minWidth: '100vw',
      height: '100vh',
      data: {
        fileResource: fileResource,
      },
    });
  }

  handleDragStart(event, resource) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = resource;
  }

  handleDragOver(event, resource) {
    event.preventDefault();
  }

  handleDrop(event, resource) {
    event.preventDefault();
    if (!this.dragNode) {
      const itemsKeys = Object.keys(event.dataTransfer.items);
      const files = [];
      itemsKeys.forEach((key) => {
        const entryFile = event.dataTransfer.items[key].webkitGetAsEntry();
        files.push(new NgxFileDropEntry(entryFile.name, entryFile));
      });
      this.onFileDropped(files, resource.folder_resource_id || resource.id);
    } else if (this.dragNode !== resource) {
      //moving a folder within another folder
      if (!this.dragNode.folder_resource_id && !resource.folder_resource_id) {
        let folder = this.dragNode;
        folder.parent_id = resource.id;
        this._foldersTabService
          .updateFolder(folder)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((folderResource) => {
            this._foldersTabService
              .getFolder(this.folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((folder) => {
                this.loading = false;
              });
          });
      } else if (this.dragNode.folder_resource_id && !resource.folder_resource_id) {
        let file = this.dragNode;
        let oldFolderId = file.folder_resource_id;
        file.folder_resource_id = resource.id;
        this._foldersTabService
          .updateFile(oldFolderId, file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((fileResource) => {
            this._foldersTabService
              .getFolder(this.folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((folder) => {
                this.loading = false;
              });
          });
      }
    }

    if (this.dragNode?.document_url && resource.document_url && this.dragNode.id !== resource.id) {
      this.files.splice(resource.rank, 0, this.files.splice(this.dragNode.rank, 1)[0]);
      this._foldersTabService
        .updateRanks(this.files, 'FileResource')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((fileResource) => {
          this._foldersTabService
            .getFolder(this.folder.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((folder) => {
              this.loading = false;
            });
        });
      this._changeDetectorRef.markForCheck();
    }
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  deleteFile(fileId): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        filter((result: boolean) => result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this._foldersTabService
          .deleteFile(this.folder.id, fileId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  deleteFolder(folderId): void {
    this.confirmService
      .openConfirmationModal({ message: 'Are you sure you wish to delete this folder?' })
      .pipe(
        filter((result: boolean) => result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this._foldersTabService
          .deleteFolder(folderId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((folder) => {
            this._foldersTabService
              .getFolder(this.folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((folder) => {
                this.loading = false;
              });
          });
      });
  }

  handleDragEnd(event) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  onFileChanged(event, folderId?) {
    this.loading = true;
    let file = event.target.files[0];
    let fileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    this._foldersTabService
      .createFile(folderId, fileResource)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fileResource) => {
        this._foldersTabService
          .uploadDocument(folderId, fileResource.id, file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((document) => {
            this.loading = false;
          });
      });
  }

  onFileDropped(files: NgxFileDropEntry[], folderId?) {
    this.loading = true;

    this._numFilesUploaded = files.length;

    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file((file: File) => {
          this.addFileToTree(droppedFile.relativePath, fileEntry, file, folderId);
        });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        this.loading = false;
      }
    }
  }

  addFileToTree(relativePath, fileEntry, file, folderId) {
    this._fileTree.push({ path: relativePath, fileEntry: fileEntry, file: file });
    if (this._fileTree.length >= this._numFilesUploaded) {
      //reset so we can use this as a counter
      this._numFilesUploaded = 0;
      this.performUpload(this.arrangeIntoTree(this._fileTree), folderId);
    }
  }

  performUpload(tree, parentFolderId = null) {
    for (let node of tree) {
      if (node.children && node.children.length > 0) {
        //we have a folder, so we should create the folder, then pass it down
        let folder = { name: node.name, parent_id: parentFolderId };
        this._foldersTabService
          .createFolder(folder, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((folder) => {
            this.performUpload(node.children, folder.id);
            if (this._numFilesUploaded <= this._fileTree.length) {
              this.loading = false;
              this._foldersTabService.getFolder(this.folder.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
            }
          });
      } else {
        let item = this._fileTree.find((item) => {
          return item.path === node.path;
        });
        if (item) {
          let fileResource = {
            folder_resource_id: parentFolderId,
            name: item.fileEntry.name,
          };
          this.loading = true;
          this._numFilesUploaded += 1;
          this._foldersTabService
            .createFile(parentFolderId, fileResource)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((fileResource) => {
              this._foldersTabService
                .uploadDocument(parentFolderId, fileResource.id, item.file)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((document) => {
                  if (this._numFilesUploaded <= this._fileTree.length) {
                    this.loading = false;
                    this._foldersTabService
                      .getFolder(this.folder.id)
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe();
                  }
                });
            });
        }
      }
    }
  }

  arrangeIntoTree(fileTree) {
    return FolderUtils.arrangeIntoTree(fileTree);
  }

  onFileOver(event) {}

  onFileLeave(event) {}

  downloadFile(file) {
    window.open(file.document_url, '_blank');
  }

  shareFolderResource(folderResourceId) {
    this._matDialog
      .open(FoldersTabFolderSharingComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { folderResourceId: folderResourceId },
        width: '40vw',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  shareFileResource(fileResource) {
    this._matDialog
      .open(FoldersTabFolderSharingComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { folderResourceId: fileResource.folder_resource_id, fileResourceId: fileResource.id },
        width: '40vw',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
